/**
 * RouteDecision — shared schema for "where Ando wants the workspace to go".
 *
 * Emitted by:
 *   1. `@ando/core/ai/queryAnalyzer` — pattern matching over chat input.
 *   2. (future) LLM router fallback when patterns under-match.
 *   3. WS `action_proposal` listener — bridges action_type → tab.
 *   4. (future) Citation click handler — bridges source.entity → tab.
 *
 * Consumed by:
 *   - `apps/client/features/workspace-router/engine/router.ts` — central
 *     `routeFromQuery` entry point that applies the decision.
 *   - `apps/client/features/workspace-router/state/routerHistoryStore.ts` —
 *     audit log for post-mortem debugging.
 *
 * Why a shared schema:
 *   - DRY: one place to evolve the routing wire shape.
 *   - Type-safe: any new `source` strategy must declare itself here.
 *   - Testable: pure data; `analyzeQuery` returns it deterministically.
 */

import { z } from "zod";
import { WorkspaceCategorySchema, type WorkspaceCategory } from "./common.js";
import { WorkspaceTabSchema, type WorkspaceTab, type WorkspaceSurface } from "./workspace.js";

/** Origin of the routing decision — drives audit + UI distinctions. */
export const RouteSourceSchema = z.enum(["pattern", "llm", "user", "action", "citation", "hint"]);
export type RouteSource = z.infer<typeof RouteSourceSchema>;

// ``outputRail`` (canvas/dashboard rail slot) was removed May 26, 2026 —
// Canvas + Dashboard are now first-class ``WorkspaceTab``s so any BE
// routing intent for them flows through ``tab`` like every other
// surface. See ``packages/core/src/domain/workspace.ts`` for the
// rationale. Removed from the schema entirely so unknown ``rail`` /
// ``outputRail`` payloads fail loudly at parse time instead of silently
// being ignored.

export const RouteDecisionSchema = z.object({
  /** Destination workspace tab (one of the 10 top-level buckets). */
  tab: WorkspaceTabSchema,
  /** Optional sub-tab id within the destination tab (e.g. `"sdi"` in finance). */
  surface: z.string().optional(),
  /** Optional filters to seed the destination tab (e.g. `{ sender: "Mario" }`). */
  filters: z.record(z.string(), z.unknown()).optional(),
  /** 0-1; below 0.6 the router intentionally NO-OPs (stay in chat). */
  confidence: z.number().min(0).max(1),
  /** Short human-readable rationale — surfaces in routerHistory + dev tools. */
  reasoning: z.string(),
  /** Origin of the decision. */
  source: RouteSourceSchema,
  /** Epoch ms when the decision was made. */
  timestamp: z.number(),
});

export type RouteDecision = z.infer<typeof RouteDecisionSchema>;

// ─── RoutingHint — BE→FE wire shape ──────────────────────────────────────
//
// Shape the backend embeds inside a chat response (`response.routing_hint`)
// to tell the client where to pivot the workspace. The BE speaks
// snake_case + the canonical ``workspace_category`` enum (inbox/docs/…);
// the client maps the category → WorkspaceTab id (conversations/documents/…)
// via ``categoryToWorkspaceTab`` so unknown categories fail loudly instead
// of silently dropping the pivot. Bare ``workspace_tab`` is accepted too
// (already in tab-id form) — useful for synthetic hints from action/citation
// flows that already know the destination tab.
//
// v1 has no equivalent — v1 chat responses don't ship routing hints
// (designed from scratch for v2). Phase 5 ApiClient.chatSend MUST surface
// this field; see TODO marker in `useComposerSubmit`.

export const RoutingHintSchema = z
  .object({
    /** Wire-shape category (BE-native enum). Mapped to a WorkspaceTab client-side. */
    workspace_category: WorkspaceCategorySchema.optional(),
    /** Direct WorkspaceTab id when the BE already speaks UI dialect. */
    workspace_tab: WorkspaceTabSchema.optional(),
    /** Sub-tab id within the destination (e.g. `"sdi"`, `"banking"`). */
    sub_tab: z.string().optional(),
    /** Optional filter map (forwarded into URL search params). */
    filters: z.record(z.string(), z.unknown()).optional(),
    /** 0-1 confidence (BE-provided). Client floors against ROUTE_CONFIDENCE_FLOOR. */
    confidence: z.number().min(0).max(1),
    /** Optional human-readable rationale forwarded into the router history. */
    reason: z.string().optional(),
  })
  .refine((h) => h.workspace_category !== undefined || h.workspace_tab !== undefined, {
    message: "RoutingHint requires either workspace_category or workspace_tab",
  });

export type RoutingHint = z.infer<typeof RoutingHintSchema>;

/**
 * Map a BE-native ``workspace_category`` to the client-side ``WorkspaceTab`` id.
 *
 * The two enums diverge intentionally:
 *   - ``WorkspaceCategory`` is the wire shape (5+work fixed by BE schema).
 *   - ``WorkspaceTab`` is the URL/UI dialect used by TanStack Router file
 *     names and the Zustand store. `inbox→conversations`, `docs→documents`
 *     are the two non-identical edges; the rest line up.
 */
const CATEGORY_TO_TAB: Record<WorkspaceCategory, WorkspaceTab> = {
  inbox: "conversations",
  calendar: "calendar",
  finance: "finance",
  docs: "documents",
  technical: "technical",
  work: "work",
};

export function categoryToWorkspaceTab(category: WorkspaceCategory): WorkspaceTab {
  return CATEGORY_TO_TAB[category];
}

/**
 * Pure converter — `RoutingHint` (BE wire shape) → `RouteDecision` (router
 * input). Stamps `source: "hint"` + caller-supplied timestamp so audit logs
 * distinguish BE-driven pivots from pattern-matcher pivots.
 */
export function routingHintToDecision(
  hint: RoutingHint,
  now: number = Date.now(),
): RouteDecision {
  const tab: WorkspaceTab =
    hint.workspace_tab ??
    (hint.workspace_category !== undefined
      ? categoryToWorkspaceTab(hint.workspace_category)
      : // Should never happen — schema refine guarantees one is present.
        "conversations");
  return {
    tab,
    confidence: hint.confidence,
    reasoning: hint.reason ?? "backend routing hint",
    source: "hint",
    timestamp: now,
    ...(hint.sub_tab !== undefined ? { surface: hint.sub_tab } : {}),
    ...(hint.filters !== undefined ? { filters: hint.filters } : {}),
  };
}

/** Confidence floor below which `routeFromQuery` returns `{ routed: false }`. */
export const ROUTE_CONFIDENCE_FLOOR = 0.6;

/** Confidence band where multiple decisions tie → disambiguation card. */
export const ROUTE_DISAMBIGUATION_DELTA = 0.1;

/** Lifespan of the "routedByAndo" pulse cue (ms). Per plan §6: 4s label. */
export const ROUTED_PULSE_TTL_MS = 4000;

/** Lifespan of the chat UndoChip countdown (ms). Per plan §4: 30s. */
export const UNDO_CHIP_TTL_MS = 30_000;

export type { WorkspaceTab, WorkspaceSurface };
